import { isNotEmpty } from '../../../common/utils/utils';
import {
	type AppRecDismissalData,
	GeneralMutationErrorResultCreator,
	MissingMutationDataResultCreator,
	MutationErrorResultCreator,
	type MutationResponse,
	type MutationResult,
	NonOkMutationResultCreator,
} from '../../graphql-result';
import { type MutationResponseBroker } from '../../response-broker';
import {
	type CacheProviderConfig,
	ResponseCacheProviderResolver,
} from '../../response-cache-provider';
import { type AppRecMutationResponse } from '../dismissal-result/types';

interface DismissalRequestConfig {
	cacheConfig?: CacheProviderConfig;
}

export default class Requester<
	T extends AppRecMutationResponse,
	R extends MutationResponse,
	D extends AppRecDismissalData,
> {
	constructor(private broker: MutationResponseBroker<T, R, D>) {}

	fetch(
		request: Request,
		{ cacheConfig }: DismissalRequestConfig = {},
	): Promise<MutationResult<D>> {
		return new Promise(async (resolve, reject) => {
			const response = await fetch(request);

			if (!response.ok) {
				return reject(new NonOkMutationResultCreator(response).makeMutationResult());
			}

			const json = await response.json();

			if (json.errors) {
				return reject(new GeneralMutationErrorResultCreator(json.errors).makeMutationResult());
			}

			const schemaResponse = json?.data?.appRecommendations as T | undefined;

			const mutationRes = this.broker.extract(schemaResponse);

			// Error handling based on missing data
			if (!isNotEmpty<R>(mutationRes)) {
				return reject(new MissingMutationDataResultCreator(response).makeMutationResult());
			}

			// Error handling based on Mutation Error
			if (mutationRes.errors?.length) {
				return reject(new MutationErrorResultCreator(mutationRes).makeMutationResult());
			}

			// Invalidate cache
			const cacheProvider = ResponseCacheProviderResolver.resolve(cacheConfig);
			await cacheProvider.delete(new Request(request.url));

			return resolve(
				this.broker.getSuccessResultCreator(response, mutationRes).makeMutationResult(),
			);
		});
	}
}
